import { useMemo } from "react";
import type { RootState } from "~/redux/store";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import classes from "./ChildCategoryNavigation.module.less";
import { IconType } from "~/common/components/icon/IconType";
import { Icon } from "~/common/components/icon/Icon";
import { Link } from "../../../common/components/Link";
import type { NavigationNode } from "~/Header/types";
import { useIsMinDesktopSize } from "~/common/hooks/useMediaQuery";
import { FixedWidthTileCarousel } from "../../../common/components/carousel/FixedWidthTileCarousel";

interface TreeNode extends NavigationNode {
  childNodes: TreeNode[];
}

interface NodeResponse {
  parentNode: NavigationNode | null;
  childNodes: TreeNode[];
}

export const ChildCategoryNavigation = () => {
  const { t } = useTranslation();
  const isDesktop = useIsMinDesktopSize();

  const navigationNodes = useSelector(selectNavigationNodes);
  const rootNode = useSelector(selectRootNode);
  const productGroupName = extractProductGroupNameFromUrl();

  const nodeResponse: NodeResponse = useMemo(() => {
    return getProductGroupChildCategories(rootNode, navigationNodes, productGroupName);
  }, [navigationNodes, rootNode, productGroupName]);

  const nodeList = nodeResponse.childNodes;
  if (!nodeList || nodeList.length === 0) {
    return;
  }

  return (
    <div className={classes.childCategoryNavigation} tracking-aux-sub-category-marker="true">
      {isDesktop && nodeResponse?.parentNode && (
        <Link
          href={nodeResponse?.parentNode?.url}
          className={classes.goBackLink}
          iconBefore={<Icon type={IconType.ChevronLeft} />}
        >
          {t(nodeResponse?.parentNode?.title)}
        </Link>
      )}
      <FixedWidthTileCarousel gap={24} itemWidth={168} items={nodeList}>
        {(node: NavigationNode) => (
          <div
            className={classes.product}
            style={{ width: `${168}px` }}
            tracking-aux-sub-category-name={t(node.title, { lng: "en" })}
            tracking-aux-sub-category-url={node.url}
          >
            <a className={classNames(classes.navigation, "scha__lc__card scha__lc__card--has-hover")} href={node.url}>
              <div className={classes.imageContainer}>
                <div className={classNames(classes.image, "scha__center-content-container")}>
                  <img src={node.bigImage?.url ?? nodeResponse?.parentNode?.bigImage?.url} />
                </div>
              </div>
              <div className={classes.title}>
                <p>{t(node.title)}</p>
              </div>
            </a>
          </div>
        )}
      </FixedWidthTileCarousel>
    </div>
  );
};

const selectNavigationNodes = (state: RootState) => {
  return state.navigation.categoryNavigation.nodes;
};
const selectRootNode = (state: RootState) => {
  return state.navigation.categoryNavigation.root;
};

const extractProductGroupNameFromUrl = () => {
  const pathName = window.location.pathname;
  const parts = pathName.split("/");
  return parts[parts.length - 1];
};

export function getProductGroupChildCategories(
  root: NavigationNode,
  nodes: Record<number, NavigationNode>,
  prodGroupName: string,
) {
  const buildNode = (node: NavigationNode): TreeNode => {
    return {
      ...node,
      childNodes: node.children
        .map((childId) => nodes[childId])
        .filter((childNode): childNode is NavigationNode => childNode !== undefined)
        .filter((childNode) => childNode.level <= 4)
        .map((childNode) => buildNode(childNode)),
    };
  };

  const findTargetAndParentNode = (
    currentNode: NavigationNode,
    groupName: string,
    parentNode: NavigationNode | null = null,
  ): { parentNode: NavigationNode | null; childNodes: NavigationNode[] } | null => {
    const basePath = currentNode?.url && currentNode?.url.split("?")[0];
    const pathArray = basePath ? basePath.split("/") : [];
    if (currentNode.url && pathArray[pathArray.length - 1] === groupName) {
      if (parentNode) {
        return {
          parentNode,
          childNodes: currentNode.children
            .map((childId) => nodes[childId])
            .filter((childNode): childNode is NavigationNode => childNode !== undefined && childNode.pimCategoryNode),
        };
      }
      return { parentNode: null, childNodes: [] };
    }
    for (const childId of currentNode.children) {
      const childNode = nodes[childId];
      if (childNode) {
        const response = findTargetAndParentNode(childNode, groupName, currentNode);
        if (response) {
          return response;
        }
      }
    }

    return null;
  };

  const result = findTargetAndParentNode(root, prodGroupName);
  if (!result || !result.childNodes) {
    return { parentNode: null, childNodes: [] };
  }

  const childNodeList = result.childNodes.map((node) => buildNode(node));
  return {
    ...result,
    childNodes: childNodeList,
  };
}
